/**
 * TONS SÉMANTIQUES — la palette des pastilles de statut.
 *
 * Avant, chaque écran redéfinissait ses propres `{ color, bg, border }` en
 * dur : autant de verts, de bleus et d'opacités légèrement différents que
 * de fichiers. Tout passe désormais par ce module : un ton = une teinte de
 * base, et les trois valeurs dérivées sont calculées de la même façon
 * partout (texte foncé, fond à 9 %, filet à 22 %).
 */

import { neutral, surfaceTokens, gradients, FONT_SANS, FONT_DISPLAY } from './tokens';

/**
 * Échelle de gris nommée (50 → 900).
 *
 * Beaucoup d'écrans piochaient des gris ardoise au hasard (#f8fafc,
 * #eef0f4, #94a3b8…). `grey(200)` dit ce qu'on veut : un cran de l'échelle,
 * pas une valeur trouvée dans un maquettage.
 */
export const grey = (step) => neutral[step] || neutral[500];

const hexToRgb = (hex) => {
  const clean = hex.replace('#', '');
  const full = clean.length === 3
    ? clean.split('').map((c) => c + c).join('')
    : clean;
  return [
    parseInt(full.slice(0, 2), 16),
    parseInt(full.slice(2, 4), 16),
    parseInt(full.slice(4, 6), 16)
  ];
};

/** Version transparente d'une couleur : utilisée pour les fonds et filets. */
export const withAlpha = (hex, alpha) => {
  const [r, g, b] = hexToRgb(hex);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

// base    = teinte pure (fonds, filets)
// text    = version foncée, lisible sur un fond dilué
// deep(er) = nuances sombres, pour les fonds de bandeau et les dégradés
const TONE_BASE = {
  neutral: { base: '#64748b', text: '#475569', deep: '#334155', deepest: '#1e293b' },
  brand: { base: '#1b2445', text: '#1b2445', deep: '#141b3a', deepest: '#0d1330', light: '#4A86CE' },
  blue: { base: '#3b82f6', text: '#2563eb', deep: '#1d4ed8', deepest: '#1e3a8a', light: '#60a5fa' },
  indigo: { base: '#6366f1', text: '#4f46e5', deep: '#312e81', deepest: '#1e1b4b', light: '#818cf8' },
  violet: { base: '#8b5cf6', text: '#7c3aed', deep: '#6d28d9', deepest: '#4c1d95', light: '#a78bfa' },
  teal: { base: '#14b8a6', text: '#0d9488', deep: '#0f766e', deepest: '#134e4a', light: '#2dd4bf' },
  emerald: { base: '#10b981', text: '#059669', deep: '#047857', deepest: '#064e3b', light: '#4ade80' },
  // Vert de marque (flèches, indicateurs de performance) + son voisin lime.
  brandGreen: { base: '#7eb627', text: '#65a30d', deep: '#4d7c0f', deepest: '#365314' },
  lime: { base: '#84cc16', text: '#65a30d', deep: '#4d7c0f', deepest: '#365314' },
  amber: { base: '#f59e0b', text: '#d97706', deep: '#b45309', deepest: '#78350f', light: '#ffb547' },
  sky: { base: '#0ea5e9', text: '#0284c7', deep: '#0369a1', deepest: '#0c4a6e', light: '#38bdf8' },
  orange: { base: '#f97316', text: '#ea580c', deep: '#c2410c', deepest: '#7c2d12', light: '#ffb476' },
  rose: { base: '#ef4444', text: '#dc2626', deep: '#b91c1c', deepest: '#7f1d1d', light: '#f87171' },
  cyan: { base: '#06b6d4', text: '#0891b2', deep: '#0e7490', deepest: '#164e63', light: '#22d3ee' },
  pink: { base: '#ec4899', text: '#db2777', deep: '#be185d', deepest: '#831843', light: '#f472b6' }
};

const build = ({ base, text, deep, deepest, light }) => ({
  base,
  color: text,
  deep: deep || text,
  deepest: deepest || deep || text,
  light: light || base,
  bg: withAlpha(base, 0.09),
  bgStrong: withAlpha(base, 0.14),
  border: withAlpha(base, 0.22),
  glow: withAlpha(base, 0.35)
});

export const TONES = Object.keys(TONE_BASE).reduce((acc, key) => {
  acc[key] = build(TONE_BASE[key]);
  return acc;
}, {});

/** Ton par nom, avec repli sur le gris neutre. */
export const getTone = (name) => TONES[name] || TONES.neutral;

/**
 * Teinte diluée d'un ton, en valeur brute.
 *
 * Contrairement à `alpha(theme.palette…)`, cette fonction n'a pas besoin du
 * thème : elle s'utilise donc partout — dans une branche de ternaire, un
 * objet de constantes, ou même un `style` DOM natif, là où un callback
 * `(theme) => …` serait impossible.
 */
export const toneAlpha = (name, opacity) => withAlpha(getTone(name).base, opacity);

/**
 * Voiles neutres.
 *
 * `lightVeil` = blanc translucide posé sur une surface sombre (bandeaux,
 * rail de navigation, cartes de verre) ; `darkVeil` = son symétrique.
 * Nommer l'intention évite les `rgba(255,255,255,0.08)` disséminés.
 */
export const lightVeil = (opacity) => withAlpha('#ffffff', opacity);
export const darkVeil = (opacity) => withAlpha('#0f172a', opacity);

/** Cran de gris dilué : `greyAlpha(200, 0.8)` pour un filet estompé. */
export const greyAlpha = (step, opacity) => withAlpha(grey(step), opacity);

/**
 * Professions de santé → ton.
 *
 * Les mêmes professions apparaissent dans une dizaine de listes ; sans
 * table commune, chaque écran leur donnait une couleur légèrement
 * différente. Une profession = une couleur, partout.
 */
const PROFESSION_TONES = {
  Infirmier: 'blue',
  'Masseur-kinésithérapeute': 'amber',
  'Sage-Femme': 'violet',
  'Pédicure-podologue': 'pink',
  Pharmacien: 'emerald',
  Médecin: 'rose',
  'Chirurgien-dentiste': 'cyan',
  Orthophoniste: 'indigo',
  Orthoptiste: 'teal'
};

/** Ton associé à une profession (gris neutre si inconnue). */
export const getProfessionTone = (profession = '') => PROFESSION_TONES[profession] || 'neutral';

/**
 * Statut d'un apprenant sur une session.
 *
 * Table canonique : le même statut donnait des couleurs différentes selon
 * l'écran (« InscritD » vert ici, indigo là). Une seule source désormais.
 */
const LEARNER_STATUS_TONES = {
  Inscrit: 'emerald',
  InscritD: 'teal',
  'En attente': 'amber',
  'À transférer': 'amber',
  Décalé: 'violet',
  'À décaler': 'rose',
  Annulation: 'rose',
  '- de 5 personnes': 'rose',
  Désinscrit: 'rose'
};

export const getLearnerStatusTone = (status = '') => LEARNER_STATUS_TONES[status] || 'neutral';

/**
 * Financeur d'une inscription.
 *
 * Repérage visuel seulement : la couleur ne porte aucun jugement, elle sert à
 * distinguer d'un coup d'œil les financeurs d'une même session.
 */
const FINANCIER_TONES = {
  DPC: 'blue',
  'OPCO-EP': 'violet',
  'Auto-financement': 'neutral',
  FAFPM: 'cyan',
  'FIFPL-PCC': 'amber',
  'FIFPL-PAIEMENT': 'orange'
};

export const getFinancierTone = (name = '') => FINANCIER_TONES[name] || 'indigo';

/** Étape de relance commerciale. */
const RELANCE_ACTION_TONES = {
  'Relance Tour 1': 'emerald',
  'Relance Tour 2': 'amber',
  'Relance Tour 3': 'rose',
  Décalage: 'indigo',
  Décalé: 'indigo'
};

export const getRelanceActionTone = (action = '') => RELANCE_ACTION_TONES[action] || 'neutral';

/**
 * Cycle de vie d'un document (convention, attestation…) :
 * signé → vert, envoyé et en attente → ambre, rien envoyé → rouge.
 * Même code couleur sur tous les écrans de suivi.
 */
export const getDocumentTone = (completed, sent) => {
  if (completed) return 'emerald';
  if (sent) return 'amber';
  return 'rose';
};

/**
 * Couleurs officielles de marques externes (réseaux sociaux…).
 * Elles n'appartiennent pas à la charte : on les garde exactes, mais
 * déclarées ici plutôt qu'éparpillées dans les fiches.
 */
export const SOCIAL_BRANDS = {
  linkedin: '#0a66c2',
  facebook: '#1877f2',
  x: '#000000',
  instagram: '#e4405f',
  youtube: '#ff0000'
};

/**
 * Identité du questionnaire public.
 *
 * C'est la seule page vue par les apprenants hors de l'application : elle
 * porte donc la charte Apteed, pas celle d'un outil tiers. Toutes ses
 * valeurs dérivent des jetons du design system — aucune couleur n'est
 * écrite dans la page elle-même.
 */
const publicSurface = surfaceTokens(true);

export const PUBLIC_FORM = {
  accent: TONES.blue.base,
  accentDark: TONES.blue.color,
  accentSoft: TONES.blue.bg,
  canvas: publicSurface.canvas,
  surface: neutral[0],
  line: publicSurface.line,
  lineStrong: publicSurface.lineStrong,
  text: publicSurface.textPrimary,
  textMuted: publicSurface.textSecondary,
  textFaint: neutral[400],
  danger: TONES.rose.deep,
  // Signature de la marque : le liseré « aurora » et le dégradé du bouton.
  aurora: gradients.aurora,
  brandTile: gradients.primaryButton,
  cta: gradients.accent,
  disabledBg: neutral[200],
  disabledText: neutral[400],
  font: FONT_SANS,
  fontDisplay: FONT_DISPLAY
};

/**
 * Rampe de chaleur (cartes et heatmaps de données).
 *
 * Échelle séquentielle unique pour toutes les visualisations de densité :
 * du sable clair au brun brûlé. `heatColor(t)` interpole pour t ∈ [0, 1].
 */
export const HEAT_RAMP = {
  low: '#fde4cf',
  mid: '#f4a259',
  high: '#9a3412',
  gradient: 'linear-gradient(90deg, #f1f5f9 0%, #fde4cf 25%, #f4a259 60%, #9a3412 100%)',
  barGradient: 'linear-gradient(90deg, #fbbf72, #f59e0b)'
};

export const heatColor = (t) => {
  const [ar, ag, ab] = hexToRgb(HEAT_RAMP.low);
  const [br, bg, bb] = hexToRgb(HEAT_RAMP.high);
  const k = Math.max(0, Math.min(1, t));
  return `rgb(${Math.round(ar + (br - ar) * k)}, ${Math.round(ag + (bg - ag) * k)}, ${Math.round(ab + (bb - ab) * k)})`;
};

/**
 * Dégradés d'avatars : la même suite partout, pour qu'un même nom donne
 * toujours la même couleur d'un écran à l'autre.
 */
export const AVATAR_GRADIENTS = [
  ['#3b82f6', '#1d4ed8'],
  ['#8b5cf6', '#6d28d9'],
  ['#06b6d4', '#0e7490'],
  ['#10b981', '#047857'],
  ['#f59e0b', '#b45309'],
  ['#ec4899', '#be185d'],
  ['#6366f1', '#4338ca'],
  ['#14b8a6', '#0f766e']
];

const hashString = (str = '') => {
  let hash = 0;
  for (let i = 0; i < str.length; i += 1) {
    // eslint-disable-next-line no-bitwise
    hash = str.charCodeAt(i) + ((hash << 5) - hash);
  }
  return Math.abs(hash);
};

/** Dégradé déterministe pour un libellé donné (avatars de listes). */
export const pickAvatarGradient = (seed = '') => AVATAR_GRADIENTS[hashString(seed) % AVATAR_GRADIENTS.length];

/** Initiales d'un intitulé, pour les avatars sans image. */
export const getInitials = (name = '', fallback = '??') => {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return fallback;
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
};

export default TONES;
